/**
 * InFlightCartRow - the New Acct "see all" list as compact rows, formatted
 * like the Online applications panel (Bill 2026-09-11: "much more space
 * efficient" than the cards). Same data helpers and the same kebab actions
 * (Close cart / Reassign) as InFlightCartCard, which stays for any other
 * surface that still wants a card.
 */
import { useEffect, useRef, useState } from 'react';
import { MoreVertical } from 'lucide-react';
import { Pill } from '@/components/primitives';
import type { CartDto, Product, Branch } from '@retail-ops/shared-types';
import {
  formatRelativeFriendly,
  humanizeBranch,
  summarizePrimaryApplicant,
  summarizeProductChips,
  summarizeOwnerNames,
  summarizeBeneficiaryTotal,
  summarizeStage,
  summarizeStatus,
} from '../utils/cartCardData';
import { operatorCode } from '@/utils/operatorCode';
import './InFlightCartRow.css';

export interface InFlightCartRowProps {
  cart: CartDto;
  products: ReadonlyArray<Product>;
  branches: ReadonlyArray<Branch>;
  onOpen: (cart: CartDto) => void;
  onCloseCart?: (cart: CartDto) => void;
  onReassign?: (cart: CartDto) => void;
}

export function InFlightCartRow({ cart, products, branches, onOpen, onCloseCart, onReassign }: InFlightCartRowProps): JSX.Element {
  const applicant = summarizePrimaryApplicant(cart);
  const chips = summarizeProductChips(cart, products);
  const stage = summarizeStage(cart.pipelineStep);
  const status = summarizeStatus(cart.status);
  // BACKLOG-1666 — who is on the cart and what it carries, so a dozen rows for
  // the same applicant can be told apart (Bill 2026-10-01).
  const owners = summarizeOwnerNames(cart);
  const beneficiaryTotal = summarizeBeneficiaryTotal(cart);
  const purpose = (cart as { purpose?: string | null }).purpose?.trim() || '';
  const showWho = owners.length > 1 || beneficiaryTotal > 0 || purpose.length > 0;
  // Core operator code (Bill 2026-09-11 ruling) - the officer's real initials.
  const officer = operatorCode(cart.officerCode);
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent): void => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  return (
    <li className="iflt-row">
      <button
        type="button"
        className="iflt-row__main"
        onClick={() => onOpen(cart)}
        aria-label={`Open application for ${applicant.label}, ${stage.label} stage, ${status.label}`}
      >
        <div className="iflt-row__name">
          {applicant.label}
          {applicant.count > 1 && <span className="iflt-row__co">+{applicant.count - 1}</span>}
          {officer && (
            <span className="iflt-row__officer" title={`Officer ${officer}`} aria-label={`Officer ${officer}`}>{officer}</span>
          )}
        </div>
        <div className="iflt-row__detail">
          <Pill variant={status.variant}>{status.label}</Pill>
          <span className="iflt-row__tag iflt-row__tag--stage">{stage.label}</span>
          {chips.map((c) => (
            <span key={c.code} className="iflt-row__tag">{c.name}</span>
          ))}
          <span className="iflt-row__tag">{humanizeBranch(cart.branch, branches)}</span>
          <span className="iflt-row__muted">updated {formatRelativeFriendly(cart.updatedAt ?? cart.createdAt)}</span>
        </div>
        {showWho && (
          <div className="iflt-row__who">
            {owners.length > 1 && (
              <span><span className="iflt-row__who-k">Owners</span>{owners.join(', ')}</span>
            )}
            {beneficiaryTotal > 0 && (
              <span><span className="iflt-row__who-k">POD</span>{beneficiaryTotal === 1 ? '1 beneficiary' : `${beneficiaryTotal} beneficiaries`}</span>
            )}
            {purpose && (
              <span><span className="iflt-row__who-k">Purpose</span>{purpose}</span>
            )}
          </div>
        )}
      </button>

      {(onCloseCart || onReassign) && (
        <div ref={menuRef} className="iflt-row__kebab">
          <button
            type="button"
            className="iflt-row__kebab-trigger"
            aria-label="More actions"
            aria-haspopup="menu"
            aria-expanded={open}
            onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
          >
            <MoreVertical size={16} aria-hidden />
          </button>
          {open && (
            <div role="menu" className="iflt-row__kebab-menu">
              {onReassign && (
                <button type="button" role="menuitem" className="iflt-row__kebab-item" onClick={() => { setOpen(false); onReassign(cart); }}>
                  Reassign…
                </button>
              )}
              {onCloseCart && (
                <button type="button" role="menuitem" className="iflt-row__kebab-item" onClick={() => { setOpen(false); onCloseCart(cart); }}>
                  Close cart…
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </li>
  );
}

export function InFlightCartList({ children }: { children: React.ReactNode }): JSX.Element {
  return <ul className="iflt-rows" aria-label="Applications in flight">{children}</ul>;
}
